import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { FormField } from "../../components/Form/FormField";
import { useConfigs } from "../../hooks/useConfigs";
import { ConfigFormData } from "../../types/config";

export const ConfigForm = () => {
  const { t } = useTranslation();
  const { configs, updateConfig } = useConfigs();

  const validationSchema = Yup.object({
    ORDER_CUTOFF_TIME: Yup.string().required(t("validation.required")),
    ORDER_EXPIRE_TIME: Yup.number().required(t("validation.required")),
    DEFAULT_AREA: Yup.string().required(t("validation.required")),
    STORE_MIN_BALANCE: Yup.number().required(t("validation.required")),
    DEFAULT_CONSUME_RATE: Yup.number().required(t("validation.required")),
    MIN_CASH_OUT: Yup.number().required(t("validation.required")),
    MAX_STORE_DISTANCE: Yup.number().required(t("validation.required")),
    HARD_MAX_STORE_DISTANCE: Yup.number().required(t("validation.required")),
    DEFAULT_LOCATION: Yup.object({
      lat: Yup.number().required(t("validation.required")),
      long: Yup.number().required(t("validation.required")),
    }),
    NOTIFICATION_EXPIRE_TIME: Yup.number().required(t("validation.required")),
    TAX_RATE: Yup.number().required(t("validation.required")),
    SALE_CANCEL_ORDER_DURATION_MINS: Yup.number().required(t("validation.required")),
    MAX_REPORT_DURATION_DAYS: Yup.number().required(t("validation.required")),
    OTP_EXPIRATION_SECS: Yup.number().required(t("validation.required")),
    GHMOBILE: Yup.object({
      version: Yup.string().required(t("validation.required")),
      url: Yup.string().url(t("validation.url")).required(t("validation.required")),
    }),
  });

  const formik = useFormik<ConfigFormData>({
    initialValues: configs || {
      ORDER_CUTOFF_TIME: "",
      ORDER_EXPIRE_TIME: 0,
      DEFAULT_AREA: "",
      STORE_MIN_BALANCE: 0,
      DEFAULT_CONSUME_RATE: 0,
      MIN_CASH_OUT: 0,
      MAX_STORE_DISTANCE: 0,
      HARD_MAX_STORE_DISTANCE: 0,
      DEFAULT_LOCATION: {
        lat: 0,
        long: 0,
      },
      NOTIFICATION_EXPIRE_TIME: 0,
      TAX_RATE: 0,
      SALE_CANCEL_ORDER_DURATION_MINS: 0,
      MAX_REPORT_DURATION_DAYS: 0,
      OTP_EXPIRATION_SECS: 0,
      GHMOBILE: {
        version: "",
        url: "",
      },
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      try {
        await updateConfig.mutateAsync({
          configs: values,
        });
      } catch (error) {
        console.error("Failed to update configs:", error);
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          label={t("configs.fields.orderCutoffTime")}
          name="ORDER_CUTOFF_TIME"
          type="text"
          formik={formik}
        />

        <FormField
          label={t("configs.fields.orderExpireTime")}
          name="ORDER_EXPIRE_TIME"
          type="number"
          formik={formik}
        />

        <FormField
          label={t("configs.fields.defaultArea")}
          name="DEFAULT_AREA"
          formik={formik}
        />

        <FormField
          label={t("configs.fields.storeMinBalance")}
          name="STORE_MIN_BALANCE"
          type="number"
          formik={formik}
        />

        <FormField
          label={t("configs.fields.defaultConsumeRate")}
          name="DEFAULT_CONSUME_RATE"
          type="number"
          formik={formik}
        />

        <FormField
          label={t("configs.fields.minCashOut")}
          name="MIN_CASH_OUT"
          type="number"
          formik={formik}
        />

        <FormField
          label={t("configs.fields.maxStoreDistance")}
          name="MAX_STORE_DISTANCE"
          type="number"
          formik={formik}
        />

        <FormField
          label={t("configs.fields.hardMaxStoreDistance")}
          name="HARD_MAX_STORE_DISTANCE"
          type="number"
          formik={formik}
        />

        <div className="space-y-4">
          <label className="block text-sm font-medium">
            {t("configs.fields.defaultLocation")}
          </label>
          <div className="grid grid-cols-2 gap-4">
            <FormField
              label={t("configs.fields.latitude")}
              name="DEFAULT_LOCATION.lat"
              type="number"
              formik={formik}
            />
            <FormField
              label={t("configs.fields.longitude")}
              name="DEFAULT_LOCATION.long"
              type="number"
              formik={formik}
            />
          </div>
        </div>

        <FormField
          label={t("configs.fields.notificationExpireTime")}
          name="NOTIFICATION_EXPIRE_TIME"
          type="number"
          formik={formik}
        />

        <FormField
          label={t("configs.fields.taxRate")}
          name="TAX_RATE"
          type="number"
          formik={formik}
        />

        <FormField
          label={t("configs.fields.saleCancelOrderDurationMins")}
          name="SALE_CANCEL_ORDER_DURATION_MINS"
          type="number"
          formik={formik}
        />

        <FormField
          label={t("configs.fields.maxReportDurationDays")}
          name="MAX_REPORT_DURATION_DAYS"
          type="number"
          formik={formik}
        />

        <FormField
          label={t("configs.fields.otpExpirationSecs")}
          name="OTP_EXPIRATION_SECS"
          type="number"
          formik={formik}
        />

        <div className="space-y-4">
          <label className="block text-sm font-medium">
            {t("configs.fields.ghMobile")}
          </label>
          <div className="space-y-4">
            <FormField
              label={t("configs.fields.ghMobileVersion")}
              name="GHMOBILE.version"
              type="text"
              formik={formik}
            />
            <FormField
              label={t("configs.fields.ghMobileUrl")}
              name="GHMOBILE.url"
              type="text"
              formik={formik}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <button
          type="submit"
          className="btn btn-primary"
          disabled={updateConfig.isPending}
        >
          {updateConfig.isPending ? t("common.saving") : t("common.save")}
        </button>
      </div>
    </form>
  );
}; 